import React, { useState, useTransition } from 'react';
import {
  Button,
  Icons,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiEye, PiEyeClosed } from 'react-icons/pi';
import { Job, ReportTemplate } from '@ais-ui/websockets';
import { useReportContext } from './ReportContext';
import { useLocalization } from '@ais-ui/locale-management';
import { hasVisualOutput } from './useManageReportProcessing';
import { useReportActions } from './useReportActions';

interface ShowLastReportButtonProps {
  report?: Job;
  reportTemplate: ReportTemplate;
}

export const ShowLastReportButton = ({
  report,
  reportTemplate,
}: ShowLastReportButtonProps) => {
  const { messages } = useLocalization();
  const { selectedJob, isWaiting } = useReportContext();
  const { showReport, hideReport } = useReportActions();

  const canShow = report && hasVisualOutput(reportTemplate.type);
  const isVisible = report && selectedJob?.uuid === report.uuid;
  
  // Use transition to show loading state immediately on click
  const [isPending, startTransition] = useTransition();
  const [localLoading, setLocalLoading] = useState(false);
  const isLoading = (isVisible && isWaiting) || isPending || localLoading;

  const handleClick = () => {
    if (!canShow || !report) return;

    if (isVisible) {
      hideReport();
    } else {
      setLocalLoading(true);
      startTransition(() => {
        showReport(report, reportTemplate);
        // Reset local loading after a short delay to allow context to update
        setTimeout(() => setLocalLoading(false), 100);
      });
    }
  };

  // No report available
  if (!report) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="flex items-center justify-center size-7">
            <PiEyeClosed className="size-4 text-muted-foreground opacity-50" />
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {messages.reportsItemNoSuccessfulReports}
        </TooltipContent>
      </Tooltip>
    );
  }

  // Report exists but no visual output
  if (!hasVisualOutput(reportTemplate.type)) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="flex items-center justify-center size-7">
            <PiEyeClosed className="size-4 text-muted-foreground" />
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {messages.reportsItemNoVisualOutput}
        </TooltipContent>
      </Tooltip>
    );
  }

  // Report with visual output - show clickable eye
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          id={`showLastReportButton${reportTemplate.tplName}`}
          size="smIcon"
          variant="ghost"
          onClick={handleClick}
          className="size-7"
        >
          {isLoading ? (
            <Icons.spinner className="size-4 animate-spin" />
          ) : isVisible ? (
            <PiEye className="size-4 text-primary" />
          ) : (
            <PiEyeClosed className="size-4" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {isLoading
          ? messages.reportsItemLoadingReport
          : isVisible
            ? messages.reportsItemHideReport
            : messages.reportsItemShowLastSuccessfulReport}
      </TooltipContent>
    </Tooltip>
  );
};
